'use client';

import { Accordion } from '@repo/ui/components/ui/accordion';
import { Alert, AlertDescription } from '@repo/ui/components/ui/alert';
import { Badge } from '@repo/ui/components/ui/badge';
import { AlertTriangle, Info, History as HistoryIcon, Loader2 } from 'lucide-react';
import { useHistory } from '../../hooks/use-history';
import { useInventoryDataProvider } from '../hooks/use-inventory-data-provider';
import { HistoryItem } from './history-item';

interface HistoryAccordionProps {
  inventaireId: number | null;
}

export function HistoryAccordion({ inventaireId }: HistoryAccordionProps) {
  const { data, isLoading, error } = useHistory(inventaireId);
  const histories = data?.histories || [];
  const { champignons, nuisibles, especes } = useInventoryDataProvider();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground font-medium">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="border-red-200 bg-red-50 shadow-sm">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">
          {error instanceof Error
            ? error.message
            : "Erreur lors du chargement de l'historique"}
        </AlertDescription>
      </Alert>
    );
  }

  if (!histories || histories.length === 0) {
    return (
      <Alert className="border-blue-200 bg-blue-50 shadow-sm">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Aucun historique de modifications trouvé pour cet inventaire.
        </AlertDescription>
      </Alert>
    );
  }

  if (!inventaireId) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-2 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <HistoryIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Historique des modifications</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              PDF via print-data (même source que print arbre), snapshot de chaque ligne
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="text-xs font-semibold px-3 py-1">
          {histories.length} entrée{histories.length > 1 ? 's' : ''}
        </Badge>
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        {histories.map((history, index) => (
          <HistoryItem
            key={history.id}
            history={history}
            index={index}
            inventaireId={inventaireId}
            champignons={champignons || []}
            nuisibles={nuisibles || []}
            especes={especes || []}
          />
        ))}
      </Accordion>
    </div>
  );
}
